import Phaser from 'phaser';
import { Depth, FONT_BODY, GAME_H, GAME_W, TILE } from '@/core/config';
import type { GameContext } from '@/core/context';
import { bus, Events } from '@/core/events';
import type { WeatherState } from '@/core/types';
import type { IWorldScene, MapData } from '@/core/types/contracts';
import { EffectsDirector } from './EffectsDirector';
import { WEATHER_STATES } from './intensity';
import { ONE_SHOT_NAMES } from './oneshots';

const FONT = { fontFamily: FONT_BODY, fontSize: '11px', color: '#f1e6c8' };
const WEATHER_LABEL: Record<WeatherState, string> = { sultry: '闷热', wind: '起风', drizzle: '细雨', rain: '大雨', 'interior-rain': '屋内雨' };
const WORLD_W = 30 * TILE, WORLD_H = 17 * TILE;

/** Mini stand-in world: sea band on top, a pond, sand + plank pier, a shell-roof shelter, three lanterns. */
function makeMap(): MapData {
  return {
    id: 'showcase-effects', width: 30, height: 17, tileSize: 32, layers: [], collision: [], exits: [], spawns: [], interactions: [], npcs: [],
    water: [{ x: 0, y: 0, w: WORLD_W, h: 3 * TILE }, { x: 20 * TILE, y: 10 * TILE, w: 5 * TILE, h: 3 * TILE }],
    rainShelter: [{ x: 4 * TILE, y: 6 * TILE, w: 7 * TILE, h: 5 * TILE }],
    lights: [
      { x: 5 * TILE, y: 7 * TILE, radius: 56, color: '#ffb347', intensity: 0.8, flicker: true },
      { x: 10 * TILE, y: 7 * TILE, radius: 56, color: '#ffb347', intensity: 0.8, flicker: true },
      { x: 16 * TILE, y: 4 * TILE, radius: 40, color: '#ffb347', intensity: 0.6, flicker: false },
    ],
  };
}

function drawGround(scene: Phaser.Scene, map: MapData): void {
  const g = scene.add.graphics().setDepth(Depth.GROUND);
  g.fillStyle(0xe2c48c, 1); g.fillRect(0, 0, WORLD_W, WORLD_H);                       // sand
  g.fillStyle(0xd4b47e, 1); for (let y = 3 * TILE; y < WORLD_H; y += TILE) for (let x = (y / TILE) % 2 ? TILE : 0; x < WORLD_W; x += TILE * 2) g.fillRect(x, y, TILE, TILE);
  for (const w of map.water ?? []) { g.fillStyle(0x1f8f8a, 1); g.fillRect(w.x, w.y, w.w, w.h); g.fillStyle(0x2aa39c, 1); g.fillRect(w.x, w.y + w.h - 6, w.w, 2); }
  g.fillStyle(0x8a5a32, 1); g.fillRect(12 * TILE, 3 * TILE, 3 * TILE, 5 * TILE);         // pier planks
  g.fillStyle(0x5a3a22, 1); for (let y = 3 * TILE; y < 8 * TILE; y += 8) g.fillRect(12 * TILE, y, 3 * TILE, 1);
  const s = (map.rainShelter ?? [])[0]!;
  g.fillStyle(0x3a2a1e, 1); g.fillRect(s.x, s.y, s.w, s.h);                                 // shelter floor (under the shell roof)
  g.fillStyle(0x6b4a2a, 1); for (let x = s.x; x < s.x + s.w; x += TILE) { g.fillRect(x + 12, s.y - 4, 6, s.h + 4); }  // posts
  for (let i = 0; i < 6; i++) { const x = 18 * TILE + i * 56, y = 12 * TILE; g.fillStyle(0x8a5a32, 1); g.fillRect(x, y - 40, 5, 40); g.fillStyle(0x3c7a3a, 1); g.fillRect(x - 10, y - 52, 25, 14); g.fillStyle(0x8ec95a, 1); g.fillRect(x - 6, y - 50, 8, 4); }
  // shell roof eave outline (the roof itself is overhead — drawn as a translucent rim so the shelter reads on screen)
  const rim = scene.add.graphics().setDepth(Depth.OVERHEAD);
  rim.lineStyle(2, 0x2b3a5c, 0.9); rim.strokeRect(s.x - 8, s.y - 12, s.w + 16, s.h + 16);
  rim.fillStyle(0x2b3a5c, 0.25); rim.fillRect(s.x - 8, s.y - 12, s.w + 16, 10);
}

/** ?showcase=effects — the real EffectsDirector attached to a stand-in world; hotkeys drive weather + one-shots. */
export function effectsShowcase(scene: Phaser.Scene): void {
  const ctx = scene.registry.get('ctx') as GameContext | undefined;
  const map = makeMap();
  scene.cameras.main.setBackgroundColor('#0b0f14');
  scene.cameras.main.setBounds(0, 0, WORLD_W, WORLD_H);
  drawGround(scene, map);
  // a stand-in player so depth sorting against the ritual glyphs / sparks is visible
  const feet = { x: 15 * TILE + 16, y: 10 * TILE + 28 };
  scene.add.rectangle(feet.x, feet.y, 32, 48, 0x2b3a5c).setOrigin(0.5, 1).setDepth(Depth.WORLD_BASE + feet.y);
  scene.add.rectangle(feet.x, feet.y - 40, 14, 12, 0xd9a34a).setOrigin(0.5, 1).setDepth(Depth.WORLD_BASE + feet.y + 1);
  scene.add.ellipse(feet.x, feet.y, 24, 8, 0x05070a, 0.4).setDepth(Depth.WORLD_BASE + feet.y - 1);

  const world = Object.assign(scene, { map, weather: 'sultry' as WeatherState, actors: new Map(), followers: [], isBlocked: () => false }) as unknown as IWorldScene;
  const director = new EffectsDirector({ seed: ctx?.seed ?? 1157, reduceMotion: ctx?.settings.reduceMotion ?? false });
  director.attach(world);
  bus.emitTyped(Events.WEATHER_CHANGE, { state: 'sultry', t: 1 });

  // weather transitions: emit the same {state, t} ramp the world scene would
  let ramp: Phaser.Time.TimerEvent | null = null;
  const setWeather = (state: WeatherState, ms = 1500) => {
    ramp?.remove(); ramp = null;
    if (ms <= 0) { (world as { weather: WeatherState }).weather = state; bus.emitTyped(Events.WEATHER_CHANGE, { state, t: 1 }); return; }
    let elapsed = 0;
    ramp = scene.time.addEvent({ delay: 100, loop: true, callback: () => {
      elapsed += 100; const t = Math.min(1, elapsed / ms);
      bus.emitTyped(Events.WEATHER_CHANGE, { state, t });
      if (t >= 1) { (world as { weather: WeatherState }).weather = state; ramp?.remove(); ramp = null; }
    } });
  };

  const glyphAt = { x: 22 * TILE, y: 8 * TILE + 8 };
  const shots: Record<string, () => Promise<void>> = {
    Q: () => director.play('ritual-pentagon', { x: glyphAt.x, y: glyphAt.y, radius: 44 }),
    W: () => director.play('ritual-triangle', { x: 7 * TILE + 16, y: 9 * TILE }),
    E: () => director.play('ritual-triangle-blackwater', { x: 7 * TILE + 16, y: 9 * TILE, ms: 2600 }),
    R: () => director.play('hit-spark', { x: feet.x + 18, y: feet.y - 26 }),
    T: () => director.play('slash-arc', { x: feet.x + 14, y: feet.y - 24, dir: 'right' }),
    Y: () => director.play('omen-glitch', { ms: 2800, strength: 0.6 }),
    U: () => director.play('fade-color', { color: '#000000', ms: 600, out: !director.shared.has('plate:fade') }),
    I: () => director.play('door-close', { ms: 900 }),
    O: () => director.play('dusk-tint', { ms: 2000 }),
    P: () => director.play('freeze-frame', { ms: 1200 }),
    L: () => director.play('lightning'),
  };
  const kb = scene.input.keyboard;
  WEATHER_STATES.forEach((w, i) => kb?.on(`keydown-${['ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'][i]}`, () => setWeather(w)));
  for (const [k, fn] of Object.entries(shots)) kb?.on(`keydown-${k}`, () => void fn());
  kb?.on('keydown-K', () => { const on = !director.getState().reduceMotion; if (ctx) ctx.updateSettings({ reduceMotion: on }); else director.setReduceMotion(on); });
  kb?.on('keydown-C', () => director.clearPersistent());
  kb?.on('keydown-ESC', () => void ctx?.toTitle());
  const cam = scene.cameras.main; cam.scrollX = 3 * TILE; cam.scrollY = 2 * TILE;
  const cursors = kb?.createCursorKeys();
  const onUpdate = () => {
    if (!cursors) return;
    const dx = (cursors.right?.isDown ? 2 : 0) - (cursors.left?.isDown ? 2 : 0), dy = (cursors.down?.isDown ? 2 : 0) - (cursors.up?.isDown ? 2 : 0);
    if (dx || dy) { cam.scrollX = Math.round(cam.scrollX + dx); cam.scrollY = Math.round(cam.scrollY + dy); }
  };
  scene.events.on(Phaser.Scenes.Events.UPDATE, onUpdate);

  // HUD (screen space)
  const hudBg = scene.add.rectangle(0, 0, GAME_W, 58, 0x05070a, 0.7).setOrigin(0).setScrollFactor(0).setDepth(Depth.UI);
  const title = scene.add.text(6, 4, '特效 showcase — EffectsDirector（天气层 + 一次性特效）', { ...FONT, fontSize: '12px', color: '#e2c48c' }).setScrollFactor(0).setDepth(Depth.UI + 1);
  const state = scene.add.text(6, 20, '', { ...FONT, fontSize: '10px', color: '#c9b89a', lineSpacing: 1 }).setScrollFactor(0).setDepth(Depth.UI + 1);
  const help = scene.add.text(6, GAME_H - 26, ['1-5 天气：闷热/起风/细雨/大雨/屋内雨   方向键 移动镜头   K 减少动态   C 清除残留   Esc 返回', 'Q 五角法阵  W 三角法阵  E 黑水  R 火花  T 剑弧  Y 不祥像素化  U 黑场  I 关门  O 暮色  P 定格  L 闪电'].join('\n'), { ...FONT, fontSize: '10px', color: '#8a8a8a' }).setScrollFactor(0).setDepth(Depth.UI + 1);
  const helpBg = scene.add.rectangle(0, GAME_H - 30, GAME_W, 30, 0x05070a, 0.7).setOrigin(0).setScrollFactor(0).setDepth(Depth.UI);
  const timer = scene.time.addEvent({ delay: 120, loop: true, callback: () => {
    const s = director.getState(); const i = s.intensity;
    state.setText([
      `weather: ${WEATHER_LABEL[s.weather.to]} (${s.weather.to}) t=${s.weather.t}   reduceMotion=${s.reduceMotion}   layers=${s.layers}   flashes=${s.flashes}   steps=${s.steps}`,
      i ? `rain ${i.rain.toFixed(2)}  wind ${i.wind.toFixed(2)}  leaves ${i.leaves.toFixed(2)}  splash ${i.splash.toFixed(2)}  ripples ${i.ripples.toFixed(2)}  haze ${i.haze.toFixed(2)}  lightning ${i.lightning.toFixed(3)}  darken ${i.darken.toFixed(2)}` : 'intensity: —',
      `active: ${s.active.join(', ') || '—'}   held: ${s.held.join(', ') || '—'}   cam ${Math.round(cam.scrollX)},${Math.round(cam.scrollY)}   particles: ${Object.entries(s.particles).filter(([k]) => !k.endsWith('Acc')).map(([k, v]) => `${k} ${v}`).join(' ') || '—'}`,
    ].join('\n'));
  } });

  (window as unknown as { __nantianEffects?: unknown }).__nantianEffects = {
    director, setWeather, play: (name: string, params?: Record<string, unknown>) => director.play(name, params ?? {}), scroll: (x: number, y: number) => { cam.scrollX = x; cam.scrollY = y; },
    names: ONE_SHOT_NAMES, state: () => director.getState(),
  };

  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    timer.remove(); ramp?.remove(); scene.events.off(Phaser.Scenes.Events.UPDATE, onUpdate);
    kb?.removeAllListeners();
    for (const o of [hudBg, title, state, help, helpBg]) o.destroy();
    director.detach();
    delete (window as unknown as { __nantianEffects?: unknown }).__nantianEffects;
  });
}
